import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion } from "@/components/ui/accordion";
import { ArrowRight, Sparkles, Calculator } from 'lucide-react';
import { pricingPlans, pricingFaqs } from '@/lib/pricingData';
import { PricingCard } from '@/components/pricing/PricingCard';
import { PricingToggle } from '@/components/pricing/PricingToggle';
import { FaqItem } from '@/components/faq/FaqItem';
import SEO from '@/components/seo/SEO';

const PricingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'RunOffTrack',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'SWPPP inspection and reporting software for construction teams.',
    offers: pricingPlans.map((plan) => ({
      '@type': 'Offer',
      name: plan.name,
      price: String(isAnnual ? plan.annualPrice : plan.monthlyPrice),
      priceCurrency: 'USD',
      category: isAnnual ? 'annual billing' : 'monthly billing',
    })),
  };
  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title="Pricing - Simple, Scalable Plans"
        description="SWPPP inspection software for independent inspectors and construction teams. Start with one active project free—no credit card required."
        canonical="/pricing"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 pb-16 md:pt-32 md:pb-24">
         <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[500px] bg-primary/5 blur-[120px] rounded-full -z-10 pointer-events-none"></div>
         
         <div className="container px-4 text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-foreground">
              Simple pricing that <br className="hidden md:block" />
              <span className="text-primary">scales with you.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              Start with one active project free. Upgrade when your team needs more sites, inspection capacity, or organization-wide controls.
            </p>

            <PricingToggle isAnnual={isAnnual} onToggle={setIsAnnual} />
         </div>
      </div>

      {/* Pricing Cards */}
      <div className="container px-4 pb-24 md:pb-32">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto items-start">
          {pricingPlans.map((plan) => (
            <PricingCard 
                key={plan.id} 
                plan={plan} 
                isAnnual={isAnnual} 
            />
          ))}
        </div>
      </div>

      {/* NEW: ROI / Fine Calculator Context */}
      <div className="bg-slate-900 text-white py-20">
        <div className="container px-4 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 rounded-full mb-6">
                <Calculator size={28} className="text-red-400" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Is the subscription worth it?</h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                Missed inspections can create serious enforcement and project risk.
                Compare the cost of compliance software vs. the risk of manual errors.
            </p>
            <Button size="lg" variant="secondary" className="gap-2" asChild>
                <Link to="/tools/fine-calculator">Calculate Your Risk <ArrowRight size={16}/></Link>
            </Button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-muted/30 py-24 border-y border-border">
        <div className="container px-4 max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-muted-foreground">Everything you need to know about billing and plans.</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {pricingFaqs.map((faq, i) => (
              <FaqItem key={i} item={faq} index={i} />
            ))}
          </Accordion>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="py-24 container px-4 text-center">
        <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-2xl mb-6">
          <Sparkles size={32} />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Still have questions?</h2>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
          Our team is happy to help you find the perfect plan for your business needs.
        </p>
        <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
           <Button variant="outline" size="lg" className="h-12 rounded-full px-6 sm:px-8" asChild>
            <Link to="/contact">Contact Sales</Link>
          </Button>
          <Button variant="ghost" size="lg" className="h-12 rounded-full px-6 hover:bg-transparent hover:text-primary sm:px-8" asChild>
            <Link to="/faq" className="flex items-center gap-2">Visit Help Center <ArrowRight size={16}/></Link>
          </Button>
        </div>
      </div>

    </div>
  );
};

export default PricingPage;
